<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import useAuthStore, { type AdaJwtCustomerUserTokenClaims } from '@/stores/authStore'

// The account switcher (fullmakter): one entry per token in the session, shown only when there is
// more than one. The title is the proxy line or the user name as is — no proper-casing here.
// The wrapper keeps the select at its natural size: .v-input is flex: 1 1 auto and would grow
// inside the drawer's column and the app bar's row.
const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const authStore = useAuthStore()

function titleFor(payload: AdaJwtCustomerUserTokenClaims | null): string {
  if (payload == null) return ''
  if (payload.proxy != null) return t('common.proxy_for_x', { x: payload.d_name.join(', ') })
  return payload.u_name
}

const items = computed(() =>
  authStore.tokensList.map((entry, index) => ({ title: titleFor(entry.payload), value: index }))
)

const selectedIndex = computed({
  get: () => authStore.tokensList.findIndex((entry) => entry.selected),
  set: (index: number) => {
    const entry = authStore.tokensList[index]
    if (entry == null) return
    authStore.selectToken(entry.token)
    // The drawer hosts this on mobile and must close on a switch.
    emit('close')
  },
})
</script>

<template>
  <div v-if="items.length > 1">
    <v-select
      v-model="selectedIndex"
      :items="items"
      :label="t('common.signed_in_as')"
      :menu-props="{ contentClass: 'custom-menu-content' }"
      density="compact"
      item-color="neutral"
      item-title="title"
      item-value="value"
      min-width="300"
      hide-details
    />
  </div>
</template>
